import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Fade,
  ThemeProvider,
  createTheme,
  alpha,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { LAUNCH_DATE } from "../../config/launchDate";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#2f9bf0", light: "#64b5f6", dark: "#0259a5" },
    secondary: { main: "#00e5ff", light: "#62efff", dark: "#008ba3" },
    background: { default: "#05070d", paper: "#0c1220" },
  },
  typography: {
    fontFamily: '"Poppins", sans-serif',
  },
});

const messages = [
  {
    lang: "English",
    text: "This application will be available to you starting from July 20, 2026",
    accent: "#2f9bf0",
  },
  {
    lang: "සිංහල",
    text: "මෙම යෙදුම 2026 ජූලි 20 වන සිට ඔබ වෙනුවෙන් විවෘතයි!",
    accent: "#00e5ff",
  },
  {
    lang: "தமிழ்",
    text: "இந்தச் செயலி ஜூலை 20, 2026 முதல் உங்கள் பயன்பாட்டுக்காக திறந்திருக்கும்!",
    accent: "#7c4dff",
  },
];

const LANG_ROTATE_MS = 5000;

const trustPoints = [
  { icon: VisibilityOffOutlinedIcon, label: "Anonymous" },
  { icon: LockOutlinedIcon, label: "Encrypted" },
  { icon: VerifiedUserOutlinedIcon, label: "Confidential" },
];

const NOISE_BG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")";

const getTimeLeft = () => {
  const diff = Math.max(0, LAUNCH_DATE.getTime() - Date.now());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
};

const TimeUnit = ({ value, label }) => (
  <Box sx={{ textAlign: "center" }}>
    <Box
      sx={{
        position: "relative",
        minWidth: { xs: 58, sm: 78, md: 88 },
        py: { xs: 1.25, sm: 1.75, md: 2 },
        borderRadius: 3,
        background: alpha("#0c1220", 0.6),
        border: `1px solid ${alpha("#7fd4ff", 0.16)}`,
        boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.06)}, 0 12px 30px ${alpha(
          "#000",
          0.35,
        )}`,
        backdropFilter: "blur(16px)",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background:
            "linear-gradient(90deg, transparent, #2f9bf0, #00e5ff, transparent)",
        },
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: { xs: "1.35rem", sm: "1.85rem", md: "2.1rem" },
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "0.02em",
          background: "linear-gradient(160deg, #ffffff 20%, #7fd4ff 100%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {String(value).padStart(2, "0")}
      </Typography>
    </Box>
    <Typography
      sx={{
        mt: 1,
        fontSize: { xs: "0.6rem", sm: "0.68rem" },
        fontWeight: 500,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: alpha("#ffffff", 0.42),
      }}
    >
      {label}
    </Typography>
  </Box>
);

const ComingSoon = () => {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft);
  const [activeLang, setActiveLang] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const timer = setTimeout(
      () => setActiveLang((prev) => (prev + 1) % messages.length),
      LANG_ROTATE_MS,
    );
    return () => clearTimeout(timer);
  }, [activeLang]);

  const orbs = useMemo(
    () => [
      { top: "-14%", left: "-10%", size: 520, color: "#0259a5", duration: 20 },
      { top: "50%", left: "78%", size: 420, color: "#00bcd4", duration: 24 },
      { top: "78%", left: "-14%", size: 380, color: "#2f9bf0", duration: 28 },
      { top: "6%", left: "60%", size: 260, color: "#7c4dff", duration: 16 },
    ],
    [],
  );

  const particles = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        size: 1 + Math.random() * 2,
        duration: 3 + Math.random() * 5,
        delay: Math.random() * 5,
      })),
    [],
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "radial-gradient(circle at 15% 10%, #0d1b30 0%, #05070d 55%, #020306 100%)",
        }}
      >
        {/* grain texture */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: NOISE_BG,
            opacity: 0.035,
            mixBlendMode: "overlay",
            pointerEvents: "none",
          }}
        />

        {/* faint grid */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(${alpha(
              "#ffffff",
              0.035,
            )} 1px, transparent 1px), linear-gradient(90deg, ${alpha(
              "#ffffff",
              0.035,
            )} 1px, transparent 1px)`,
            backgroundSize: "56px 56px",
            maskImage:
              "radial-gradient(ellipse at center, black 0%, transparent 72%)",
            pointerEvents: "none",
          }}
        />

        {/* slow-rotating aurora sweep */}
        <Box
          sx={{
            position: "absolute",
            inset: "-40%",
            background: `conic-gradient(from 0deg, ${alpha(
              "#2f9bf0",
              0.16,
            )}, transparent 25%, ${alpha("#7c4dff", 0.14)} 50%, transparent 75%, ${alpha(
              "#00e5ff",
              0.14,
            )})`,
            filter: "blur(60px)",
            mixBlendMode: "screen",
            animation: "rotateAurora 40s linear infinite",
            pointerEvents: "none",
            "@keyframes rotateAurora": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          }}
        />

        {/* twinkling starfield */}
        {particles.map((p) => (
          <Box
            key={p.id}
            sx={{
              position: "absolute",
              top: p.top,
              left: p.left,
              width: p.size,
              height: p.size,
              borderRadius: "50%",
              backgroundColor: "#ffffff",
              opacity: 0.15,
              animation: `twinkle ${p.duration}s ease-in-out ${p.delay}s infinite`,
              pointerEvents: "none",
              "@keyframes twinkle": {
                "0%, 100%": { opacity: 0.1, transform: "scale(1)" },
                "50%": { opacity: 0.85, transform: "scale(1.6)" },
              },
            }}
          />
        ))}

        {orbs.map((orb, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: orb.top,
              left: orb.left,
              width: orb.size,
              height: orb.size,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(
                orb.color,
                0.4,
              )} 0%, ${alpha(orb.color, 0)} 70%)`,
              filter: "blur(10px)",
              animation: `floatOrb ${orb.duration}s ease-in-out infinite`,
              pointerEvents: "none",
              "@keyframes floatOrb": {
                "0%, 100%": { transform: "translate(0, 0) scale(1)" },
                "50%": { transform: "translate(30px, -30px) scale(1.08)" },
              },
            }}
          />
        ))}

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 2, py: { xs: 6, md: 8 } }}>
          <Fade in timeout={700}>
            <Stack spacing={{ xs: 4, sm: 5 }} alignItems="center" textAlign="center">
              {/* logo with soft glow */}
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    position: "absolute",
                    inset: -24,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${alpha(
                      "#2f9bf0",
                      0.35,
                    )} 0%, transparent 70%)`,
                    filter: "blur(6px)",
                  }}
                />
                <Box
                  component="img"
                  src="/assets/siteLogo/logo.png"
                  alt="ApeSalli"
                  sx={{
                    position: "relative",
                    height: { xs: 76, sm: 92 },
                    objectFit: "contain",
                    animation: "floatLogo 6s ease-in-out infinite",
                    "@keyframes floatLogo": {
                      "0%, 100%": { transform: "translateY(0)" },
                      "50%": { transform: "translateY(-8px)" },
                    },
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </Box>

              {/* eyebrow */}
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box sx={{ position: "relative", width: 8, height: 8 }}>
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "50%",
                      backgroundColor: "#00e5ff",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "50%",
                      backgroundColor: "#00e5ff",
                      animation: "ping 1.8s cubic-bezier(0,0,0.2,1) infinite",
                      "@keyframes ping": {
                        "0%": { transform: "scale(1)", opacity: 0.7 },
                        "75%, 100%": { transform: "scale(2.4)", opacity: 0 },
                      },
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: alpha("#7fd4ff", 0.85),
                  }}
                >
                  Coming Soon
                </Typography>
              </Stack>

              {/* headline */}
              <Box>
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "2rem", sm: "2.75rem", md: "3.25rem" },
                    lineHeight: 1.12,
                    letterSpacing: "-0.03em",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #cfeaff 45%, #2f9bf0 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Report Grand Corruption
                </Typography>
                <Typography
                  sx={{
                    mt: 1.5,
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: alpha("#ffffff", 0.55),
                    maxWidth: 480,
                    mx: "auto",
                    lineHeight: 1.7,
                  }}
                >
                  A secure platform to submit encrypted, anonymous reports
                  directly to verified TISL officers.
                </Typography>
              </Box>

              {/* trust points */}
              <Stack direction="row" spacing={{ xs: 2, sm: 3.5 }} flexWrap="wrap" justifyContent="center">
                {trustPoints.map(({ icon: Icon, label }) => (
                  <Stack key={label} direction="row" spacing={0.75} alignItems="center">
                    <Icon sx={{ fontSize: "1rem", color: alpha("#7fd4ff", 0.75) }} />
                    <Typography
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 500,
                        color: alpha("#ffffff", 0.55),
                      }}
                    >
                      {label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              {/* countdown */}
              <Box sx={{ width: "100%", pt: { xs: 1, sm: 2 } }}>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: alpha("#ffffff", 0.4),
                    mb: 2,
                  }}
                >
                  Launching July 20, 2026
                </Typography>
                <Stack direction="row" spacing={{ xs: 1.25, sm: 2 }} justifyContent="center">
                  <TimeUnit value={timeLeft.days} label="Days" />
                  <TimeUnit value={timeLeft.hours} label="Hours" />
                  <TimeUnit value={timeLeft.minutes} label="Minutes" />
                  <TimeUnit value={timeLeft.seconds} label="Seconds" />
                </Stack>
              </Box>

              {/* language switcher */}
              <Box sx={{ width: "100%", pt: { xs: 2, sm: 3 } }}>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: alpha("#ffffff", 0.4),
                    mb: 2.5,
                  }}
                >
                  Available In
                </Typography>

                <Stack direction="row" spacing={1.25} justifyContent="center" sx={{ mb: 3 }}>
                  {messages.map((m, i) => {
                    const isActive = activeLang === i;
                    return (
                      <Box
                        key={m.lang}
                        onClick={() => setActiveLang(i)}
                        role="button"
                        tabIndex={0}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.85,
                          px: { xs: 1.5, sm: 2 },
                          py: 0.85,
                          borderRadius: 999,
                          border: `1px solid ${
                            isActive ? alpha(m.accent, 0.6) : alpha("#ffffff", 0.12)
                          }`,
                          backgroundColor: isActive
                            ? alpha(m.accent, 0.14)
                            : "transparent",
                          transition: "all 0.25s ease",
                          "&:hover": {
                            borderColor: alpha(m.accent, 0.5),
                            backgroundColor: alpha(m.accent, 0.1),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: m.accent,
                            opacity: isActive ? 1 : 0.4,
                            transition: "opacity 0.25s ease",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: { xs: "0.75rem", sm: "0.8rem" },
                            fontWeight: 600,
                            color: isActive ? "#ffffff" : alpha("#ffffff", 0.5),
                          }}
                        >
                          {m.lang}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>

                <Box
                  sx={{
                    position: "relative",
                    minHeight: { xs: 92, sm: 72 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: { xs: 1, sm: 6 },
                  }}
                >
                  <Fade key={activeLang} in timeout={450}>
                    <Typography
                      sx={{
                        fontSize: { xs: "0.95rem", sm: "1.1rem" },
                        fontWeight: 500,
                        lineHeight: 1.65,
                        color: alpha("#ffffff", 0.92),
                        maxWidth: 560,
                      }}
                    >
                      {messages[activeLang].text}
                    </Typography>
                  </Fade>
                </Box>

                <Box
                  sx={{
                    width: 180,
                    height: 3,
                    mx: "auto",
                    mt: 2,
                    borderRadius: 2,
                    backgroundColor: alpha("#ffffff", 0.08),
                    overflow: "hidden",
                  }}
                >
                  <Box
                    key={activeLang}
                    sx={{
                      height: "100%",
                      background: `linear-gradient(90deg, ${messages[activeLang].accent}, #ffffff)`,
                      animation: `langProgress ${LANG_ROTATE_MS}ms linear forwards`,
                      "@keyframes langProgress": {
                        from: { width: "0%" },
                        to: { width: "100%" },
                      },
                    }}
                  />
                </Box>
              </Box>

              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: alpha("#ffffff", 0.3),
                  pt: { xs: 1, sm: 2 },
                }}
              >
                &copy; {new Date().getFullYear()} ApeSalli. All rights reserved.
              </Typography>
            </Stack>
          </Fade>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ComingSoon;
